import { logger } from "@/utils/logger";

/**
 * Utility to generate a stable device fingerprint based on browser characteristics.
 * This allows identifying a device across IP address changes (WiFi -> Cellular)
 * while preventing token theft via XSS (requires identical hardware/browser).
 */

export async function generateDeviceFingerprint(): Promise<string> {
    try {
        const [canvas, audio] = await Promise.all([
            getCanvasFingerprint(),
            getAudioFingerprint()
        ]);

        const fingerprint = {
            // Timezone (very stable)
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,

            // Hardware (very stable)
            cores: navigator.hardwareConcurrency || 0,
            // @ts-ignore - deviceMemory is non-standard but supported in Chrome/Edge
            memory: navigator.deviceMemory || 0,

            // WebGL fingerprint (unique per GPU driver)
            webgl: getWebGLFingerprint(),

            // Canvas fingerprint (unique to GPU rendering)
            canvas: canvas,

            // Audio fingerprint (unique to sound card/driver)
            audio: audio,

            // Screen details (stable per device)
            screen: `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`,

            // Platform
            platform: navigator.platform,

            // Languages (stable)
            languages: navigator.languages ? navigator.languages.join(',') : navigator.language,
        };

        // Return as base64 string to be sent to server
        return btoa(JSON.stringify(fingerprint));
    } catch (error) {
        logger.error('Error generating device fingerprint:', error);
        // Fallback to basic info if generation fails
        return btoa(JSON.stringify({
            userAgent: navigator.userAgent,
            timestamp: Date.now()
        }));
    }
}

// Audio fingerprinting (unique to sound hardware/OS processing)
async function getAudioFingerprint(): Promise<string> {
    try {
        // Use OfflineAudioContext for a silent, deterministic calculation
        const audioCtx = new (window.OfflineAudioContext || (window as any).webkitOfflineAudioContext)(1, 44100, 44100);
        if (!audioCtx) return 'unsupported';

        const oscillator = audioCtx.createOscillator();
        const compressor = audioCtx.createDynamicsCompressor();

        oscillator.type = 'triangle';
        oscillator.frequency.setValueAtTime(10000, audioCtx.currentTime);

        compressor.threshold.setValueAtTime(-50, audioCtx.currentTime);
        compressor.knee.setValueAtTime(40, audioCtx.currentTime);
        compressor.ratio.setValueAtTime(12, audioCtx.currentTime);
        compressor.attack.setValueAtTime(0, audioCtx.currentTime);
        compressor.release.setValueAtTime(0.25, audioCtx.currentTime);

        oscillator.connect(compressor);
        compressor.connect(audioCtx.destination);

        oscillator.start(0);
        const renderedBuffer = await audioCtx.startRendering();
        const data = renderedBuffer.getChannelData(0);

        // Calculate a signature from the rendered buffer
        let sum = 0;
        for (let i = 4500; i < 5000; i++) {
            sum += Math.abs(data[i]);
        }

        return sum.toString().slice(0, 16);
    } catch (e) {
        return 'error';
    }
}

// Canvas fingerprint (unique to GPU)
async function getCanvasFingerprint(): Promise<string> {
    try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) return '';

        // Draw unique pattern including emojis for OS-specific rendering
        ctx.textBaseline = 'top';
        ctx.font = '14px Arial';
        ctx.fillStyle = '#f60';
        ctx.fillRect(125, 1, 62, 20);
        ctx.fillStyle = '#069';
        ctx.fillText('VasisPro 🏳️‍🌈 👨‍👩‍👧‍👦', 2, 15);
        ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
        ctx.fillText('VasisPro 🏳️‍🌈 👨‍👩‍👧‍👦', 4, 17);

        const dataURL = canvas.toDataURL();
        const encoder = new TextEncoder();
        const data = encoder.encode(dataURL);
        const hashBuffer = await crypto.subtle.digest('SHA-256', data);

        return Array.from(new Uint8Array(hashBuffer))
            .map(b => b.toString(16).padStart(2, '0'))
            .join('')
            .slice(0, 16);
    } catch (e) {
        return 'error';
    }
}

// WebGL fingerprint
function getWebGLFingerprint(): string {
    try {
        const canvas = document.createElement('canvas');
        const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
        if (!gl) return '';

        // @ts-ignore
        const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
        if (!debugInfo) return '';

        // @ts-ignore
        const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
        // @ts-ignore
        const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);

        return `${vendor}|${renderer}`.slice(0, 32);
    } catch (e) {
        return 'error';
    }
}
